import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { getTranslatedError } from '../../utils/errorTranslator';
import { joinMonitoring, leaveMonitoring, getSocket } from '../../services/socketService';
import { ReloadOutlined, WifiOutlined, DisconnectOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { message } from 'antd';

const WebSocketClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Обработчик обновлений WebSocket клиентов через WebSocket
  const handleWebSocketClients = React.useCallback((data) => {
    console.log('WebSocket clients received:', data);
    setError('');
    setLoading(false);
    
    if (data && Array.isArray(data.clients)) {
      setClients(data.clients);
    } else if (Array.isArray(data)) {
      setClients(data);
    } else if (data && data.clients && Array.isArray(data.clients)) {
      setClients(data.clients);
    } else {
      setClients([]);
    }
  }, []);

  useEffect(() => {
    const socket = getSocket();
    
    // Функция для подписки на события
    const subscribeToEvents = (currentSocket) => {
      if (!currentSocket) return;
      
      // Подписываемся на мониторинг (если еще не подписаны)
      joinMonitoring();
      
      // Подписываемся на обновления списка клиентов
      currentSocket.on('websocket_clients', handleWebSocketClients);
    };
    
    // Функция для отписки от событий
    const unsubscribeFromEvents = (currentSocket) => {
      if (!currentSocket) return;
      
      leaveMonitoring();
      currentSocket.off('websocket_clients', handleWebSocketClients);
    };
    
    // Подписываемся на WebSocket мониторинг
    if (socket && socket.connected) {
      subscribeToEvents(socket);
    } else {
      // Используем событие 'connect' вместо интервала
      const onConnect = () => {
        const currentSocket = getSocket();
        if (currentSocket && currentSocket.connected) {
          subscribeToEvents(currentSocket);
        }
      };
      
      if (socket) {
        socket.on('connect', onConnect);
      }
      
      return () => {
        if (socket) {
          socket.off('connect', onConnect);
        }
      };
    }
    
    return () => {
      // Отписываемся от мониторинга при размонтировании
      const currentSocket = getSocket();
      unsubscribeFromEvents(currentSocket);
    };
  }, [handleWebSocketClients]);

  // Мемоизированные функции форматирования
  const formatDate = React.useCallback((dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return dateString;
    }
  }, []);

  const calculateUptime = React.useCallback((connectedAt) => {
    if (!connectedAt) return '—';
    try {
      const connected = new Date(connectedAt);
      const now = new Date();
      const diff = now - connected;
      
      const seconds = Math.floor(diff / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);
      
      if (days > 0) {
        return `${days}д ${hours % 24}ч ${minutes % 60}м`;
      } else if (hours > 0) {
        return `${hours}ч ${minutes % 60}м ${seconds % 60}с`;
      } else if (minutes > 0) {
        return `${minutes}м ${seconds % 60}с`;
      } else {
        return `${seconds}с`;
      }
    } catch {
      return '—';
    }
  }, []);

  const getClientInfo = (client) => {
    // Поддержка разных форматов ответа от сервера
    return {
      id: client.socketId || client.id || client.clientId || '—',
      userId: client.userId || client.user?.id || '—',
      userLogin: client.login || client.userLogin || client.user?.login || client.user?.name || 'unauthenticated',
      role: client.role || client.user?.role || '—',
      ip: client.ip || client.remoteAddress || client.address || '—',
      userAgent: client.userAgent || client.headers?.['user-agent'] || '—',
      connectedAt: client.connectedAt || client.connected_at || client.createdAt || '—',
      authenticatedAt: client.authenticatedAt || client.authenticated_at || '—',
      connectionDurationSeconds: client.connectionDurationSeconds || 0,
      rooms: client.rooms || client.subscribedRooms || [],
      isAuthenticated: client.userId ? true : (client.isAuthenticated !== undefined ? client.isAuthenticated : false),
    };
  };

  return (
    <div>
      <Card className="mb-2">
        <Card.Header className="p-2">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0" style={{ fontSize: '16px' }}>
              <WifiOutlined className="me-2" />
              Подключенные WebSocket клиенты
            </h6>
            <div className="d-flex gap-2 align-items-center">
              <Badge bg="success" className="me-2" style={{ fontSize: '10px' }}>
                <WifiOutlined /> WebSocket
              </Badge>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-2">
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-2">
              {error}
            </Alert>
          )}

          {loading && clients.length === 0 ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted" style={{ fontSize: '12px' }}>Загрузка клиентов...</p>
            </div>
          ) : (
            <>
              <div className="mb-2">
                <Badge bg="info" style={{ fontSize: '12px', padding: '4px 8px' }}>
                  Всего подключено: {clients.length}
                </Badge>
                {clients.filter(c => getClientInfo(c).isAuthenticated).length > 0 && (
                  <Badge bg="success" className="ms-2" style={{ fontSize: '12px', padding: '4px 8px' }}>
                    Авторизованных: {clients.filter(c => getClientInfo(c).isAuthenticated).length}
                  </Badge>
                )}
              </div>

              {clients.length === 0 ? (
                <Alert variant="info" className="mb-0">
                  Нет подключенных клиентов
                </Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th style={{ fontSize: '12px' }}>Socket ID</th>
                        <th style={{ fontSize: '12px' }}>Пользователь</th>
                        <th style={{ fontSize: '12px' }}>Роль</th>
                        <th style={{ fontSize: '12px' }}>IP адрес</th>
                        <th style={{ fontSize: '12px' }}>Подключен</th>
                        <th style={{ fontSize: '12px' }}>Авторизован</th>
                        <th style={{ fontSize: '12px' }}>Длительность</th>
                        <th style={{ fontSize: '12px' }}>Комнаты</th>
                        <th style={{ fontSize: '12px' }}>Статус</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.map((client, idx) => {
                        const info = getClientInfo(client);
                        return (
                          <tr key={info.id || idx}>
                            <td style={{ fontSize: '11px', fontFamily: 'monospace' }}>
                              {info.id.length > 20 ? `${info.id.substring(0, 20)}...` : info.id}
                            </td>
                            <td style={{ fontSize: '12px' }}>
                              {info.userId !== '—' ? (
                                <div>
                                  <strong>{info.userLogin}</strong>
                                  <div style={{ fontSize: '10px', color: '#6c757d' }}>
                                    ID: {info.userId}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-muted">{info.userLogin}</span>
                              )}
                            </td>
                            <td style={{ fontSize: '11px' }}>
                              {info.role !== '—' ? (
                                <Badge bg={info.role === 'admin' ? 'danger' : 'primary'} style={{ fontSize: '10px' }}>
                                  {info.role}
                                </Badge>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td style={{ fontSize: '11px', fontFamily: 'monospace' }}>
                              {info.ip}
                            </td>
                            <td style={{ fontSize: '11px' }}>
                              {formatDate(info.connectedAt)}
                            </td>
                            <td style={{ fontSize: '11px' }}>
                              {info.authenticatedAt !== '—' ? formatDate(info.authenticatedAt) : '—'}
                            </td>
                            <td style={{ fontSize: '11px' }}>
                              <div className="d-flex align-items-center">
                                <ClockCircleOutlined className="me-1" style={{ fontSize: '12px' }} />
                                {info.connectionDurationSeconds > 0 
                                  ? `${Math.floor(info.connectionDurationSeconds / 3600)}ч ${Math.floor((info.connectionDurationSeconds % 3600) / 60)}м ${info.connectionDurationSeconds % 60}с`
                                  : calculateUptime(info.connectedAt)}
                              </div>
                            </td>
                            <td style={{ fontSize: '11px' }}>
                              {info.rooms && info.rooms.length > 0 ? (
                                <div>
                                  {info.rooms.slice(0, 2).map((room, i) => (
                                    <Badge key={i} bg="secondary" className="me-1" style={{ fontSize: '9px' }}>
                                      {room}
                                    </Badge>
                                  ))}
                                  {info.rooms.length > 2 && (
                                    <Badge bg="light" text="dark" style={{ fontSize: '9px' }}>
                                      +{info.rooms.length - 2}
                                    </Badge>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td>
                              {info.isAuthenticated ? (
                                <Badge bg="success" style={{ fontSize: '10px' }}>
                                  Авторизован
                                </Badge>
                              ) : (
                                <Badge bg="warning" text="dark" style={{ fontSize: '10px' }}>
                                  Гость
                                </Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default WebSocketClients;

